import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Filter, X, Wine, ChevronDown, ChevronUp, Edit, Trash2, Plus, HelpCircle, RefreshCw, Upload, ImageIcon, Download, BarChart2, CheckSquare, Square } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LibraryAutocomplete } from "@/components/LibraryAutocomplete";
import QuestionsSection from "@/components/QuestionsSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CocktailsPage() {
  const [cocktails, setCocktails] = useState([]);
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [baseSpirit, setBaseSpirit] = useState("");
  const [ingredient, setIngredient] = useState("");
  const [selectedCocktail, setSelectedCocktail] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [questionOptions, setQuestionOptions] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [libraryItems, setLibraryItems] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const canEdit = currentUser?.role === "admin" || currentUser?.role === "org_admin" || currentUser?.role === "system_admin";
  const isSystemAdmin = currentUser?.role === "system_admin";

  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const longPressTimer = useRef(null);
  const suppressNextClick = useRef(false);

  const toggleSelectionMode = () => {
    setSelectionMode(prev => {
      if (prev) setSelectedIds(new Set());
      return !prev;
    });
  };

  const toggleSelectCocktail = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => setSelectedIds(new Set(cocktails.map(c => c.id)));
  const clearSelection = () => setSelectedIds(new Set());

  const startLongPress = (cocktail) => {
    longPressTimer.current = setTimeout(() => {
      setSelectionMode(true);
      setSelectedIds(new Set([cocktail.id]));
      suppressNextClick.current = true;
    }, 550);
  };
  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleCardClick = (cocktail) => {
    if (suppressNextClick.current) {
      suppressNextClick.current = false;
      return;
    }
    if (selectionMode) {
      toggleSelectCocktail(cocktail.id);
    } else {
      setSelectedCocktail(cocktail);
    }
  };

  const handleCardContextMenu = (e, cocktail) => {
    e.preventDefault();
    if (!selectionMode) {
      setSelectionMode(true);
      setSelectedIds(new Set([cocktail.id]));
    } else {
      toggleSelectCocktail(cocktail.id);
    }
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const res = await axios.post(`${API}/cocktails/bulk-delete`, { cocktail_ids: Array.from(selectedIds) });
      toast.success(`Deleted ${res.data.deleted_count} cocktail${res.data.deleted_count === 1 ? "" : "s"}`);
      if (res.data.skipped?.length) {
        toast.error(`${res.data.skipped.length} couldn't be deleted`);
      }
      setSelectedIds(new Set());
      setSelectionMode(false);
      setShowBulkDeleteConfirm(false);
      fetchCocktails();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete cocktails");
    } finally {
      setBulkDeleting(false);
    }
  };

  const updateVisibility = async (cocktailId, visibility) => {
    try {
      const res = await axios.patch(`${API}/cocktails/${cocktailId}/visibility`, { visibility });
      // Refresh local list + selected
      setCocktails(prev => prev.map(c => c.id === cocktailId ? { ...c, visibility: res.data.visibility } : c));
      setSelectedCocktail(prev => prev && prev.id === cocktailId ? { ...prev, visibility: res.data.visibility } : prev);
      toast.success(visibility === "private" ? "Now private to your org" : visibility === "public" ? "Now visible to everyone" : "Promoted to system content");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update visibility");
    }
  };

  useEffect(() => {
    fetchFilters();
    fetchCocktails();
    fetchLibrary();
    axios.get(`${API}/auth/me`).then(res => setCurrentUser(res.data)).catch(() => {});
  }, []);

  const fetchLibrary = async () => {
    try {
      const res = await axios.get(`${API}/library`);
      setLibraryItems(res.data);
    } catch (err) {
      console.error("Failed to fetch library:", err);
    }
  };

  const getLibraryByType = (type) => libraryItems.filter(i => i.type === type);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchCocktails();
    }, 300);
    return () => clearTimeout(debounce);
  }, [search, category, baseSpirit, ingredient]);

  const fetchFilters = async () => {
    try {
      const response = await axios.get(`${API}/cocktails-meta/filters`);
      setFilters(response.data);
    } catch (error) {
      console.error("Failed to fetch filters:", error);
    }
  };

  const fetchCocktails = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (category) params.append("category", category);
      if (baseSpirit) params.append("base_spirit", baseSpirit);
      if (ingredient) params.append("ingredient", ingredient);
      
      const response = await axios.get(`${API}/cocktails?${params.toString()}`);
      setCocktails(response.data);
    } catch (error) {
      console.error("Failed to fetch cocktails:", error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setCategory("");
    setBaseSpirit("");
    setIngredient("");
    setSearch("");
  };

  const hasActiveFilters = category || baseSpirit || ingredient;

  const categoryFileInputRef = useRef(null);
  const [categoryExporting, setCategoryExporting] = useState(false);
  const [categoryImporting, setCategoryImporting] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const [showImportSummary, setShowImportSummary] = useState(false);

  const exportCategoryQuestionsCsv = async () => {
    if (!category) {
      toast.error("Select a category first");
      return;
    }
    setCategoryExporting(true);
    try {
      const res = await axios.get(`${API}/cocktails/questions/export-csv`, {
        params: { category },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      const safeName = category.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      a.download = `${safeName}-questions.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error("No questions found for this category yet");
      } else {
        toast.error("Failed to export questions");
      }
    } finally {
      setCategoryExporting(false);
    }
  };

  const triggerCategoryImport = () => {
    if (!category) {
      toast.error("Select a category first");
      return;
    }
    categoryFileInputRef.current?.click();
  };

  const handleCategoryImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setCategoryImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/cocktails/questions/import-csv`, formData, {
        params: { category },
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportSummary(res.data);
      setShowImportSummary(true);
      fetchCocktails();
      if (res.data.updated?.length) {
        toast.success(`Updated questions for ${res.data.updated.length} cocktail${res.data.updated.length === 1 ? "" : "s"}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to import questions");
    } finally {
      setCategoryImporting(false);
    }
  };

  const fetchQuestionOptions = useCallback(async () => {
    if (questionOptions) return;
    try {
      const res = await axios.get(`${API}/question-options`);
      setQuestionOptions(res.data);
    } catch (err) {
      console.error("Failed to fetch question options:", err);
    }
  }, [questionOptions]);

  const openEditMode = (cocktail) => {
    setEditForm({
      ...cocktail,
      ingredients: cocktail.ingredients?.length > 0 
        ? cocktail.ingredients 
        : [{ name: "", measurement: "", brand: "" }],
      questions: cocktail.questions || [],
      batch_ingredient_name: cocktail.batch_ingredient_name || null,
      batch_breakdown: cocktail.batch_breakdown?.length > 0 ? cocktail.batch_breakdown : [{ name: "", measurement: "", brand: "" }],
    });
    setEditMode(true);
    fetchQuestionOptions();
  };

  const closeEditMode = () => {
    setEditMode(false);
    setEditForm(null);
  };

  const addIngredient = () => {
    setEditForm(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { name: "", measurement: "", brand: "" }]
    }));
  };

  const removeIngredient = (index) => {
    setEditForm(prev => {
      const removedName = prev.ingredients[index]?.name;
      const wasBatch = removedName && prev.batch_ingredient_name === removedName;
      return {
        ...prev,
        ingredients: prev.ingredients.filter((_, i) => i !== index),
        batch_ingredient_name: wasBatch ? null : prev.batch_ingredient_name,
      };
    });
  };

  const updateIngredient = (index, field, value) => {
    setEditForm(prev => {
      const oldName = prev.ingredients[index].name;
      const ingredients = prev.ingredients.map((ing, i) =>
        i === index ? { ...ing, [field]: value } : ing
      );
      // Keep batch_ingredient_name in sync if the batch's own ingredient row is renamed
      const batch_ingredient_name = (field === "name" && prev.batch_ingredient_name === oldName)
        ? value
        : prev.batch_ingredient_name;
      return { ...prev, ingredients, batch_ingredient_name };
    });
  };

  const toggleBatchIngredient = (ing) => {
    setEditForm(prev => ({
      ...prev,
      batch_ingredient_name: prev.batch_ingredient_name === ing.name ? null : ing.name,
    }));
  };

  const addBatchBreakdownItem = () => {
    setEditForm(prev => ({
      ...prev,
      batch_breakdown: [...(prev.batch_breakdown || []), { name: "", measurement: "", brand: "" }],
    }));
  };

  const removeBatchBreakdownItem = (index) => {
    setEditForm(prev => ({
      ...prev,
      batch_breakdown: prev.batch_breakdown.filter((_, i) => i !== index),
    }));
  };

  const updateBatchBreakdownItem = (index, field, value) => {
    setEditForm(prev => ({
      ...prev,
      batch_breakdown: prev.batch_breakdown.map((ing, i) =>
        i === index ? { ...ing, [field]: value } : ing
      ),
    }));
  };

  const updateQuestion = (qIdx, field, value) => {
    setEditForm(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => i === qIdx ? { ...q, [field]: value } : q)
    }));
  };

  const updateWrongAnswer = (qIdx, waIdx, value) => {
    setEditForm(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => {
        if (i !== qIdx) return q;
        const wrong = [...(q.wrong_answers || [])];
        wrong[waIdx] = value;
        return { ...q, wrong_answers: wrong };
      })
    }));
  };

  const addQuestion = () => {
    setEditForm(prev => ({
      ...prev,
      questions: [
        ...(prev.questions || []),
        { question_text: "", correct_answer: "", wrong_answers: ["", "", ""] },
      ],
    }));
  };

  const removeQuestion = (qIdx) => {
    setEditForm(prev => ({
      ...prev,
      questions: (prev.questions || []).filter((_, i) => i !== qIdx),
    }));
  };

  const moveQuestion = (qIdx, direction) => {
    setEditForm(prev => {
      const list = [...(prev.questions || [])];
      const target = qIdx + direction;
      if (target < 0 || target >= list.length) return prev;
      [list[qIdx], list[target]] = [list[target], list[qIdx]];
      return { ...prev, questions: list };
    });
  };

  const [showBulk, setShowBulk] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  const downloadQuestionsCsv = () => {
    const qs = editForm.questions || [];
    if (!qs.length) {
      toast.error("No questions to export");
      return;
    }
    // Escape any field that contains comma, quote, or newline.
    const esc = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = [["Question", "Correct", "Wrong 1", "Wrong 2", "Wrong 3"]];
    qs.forEach(q => {
      const w = [...(q.wrong_answers || []), "", "", ""].slice(0, 3);
      rows.push([q.question_text || "", q.correct_answer || "", w[0], w[1], w[2]]);
    });
    const csv = rows.map(r => r.map(esc).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const safeName = (editForm.name || "cocktail").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    a.download = `${safeName}-questions.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const openAnalytics = async () => {
    if (!editForm.id) {
      toast.error("Save the cocktail first");
      return;
    }
    setShowAnalytics(true);
    setLoadingAnalytics(true);
    try {
      const res = await axios.get(`${API}/cocktails/${editForm.id}/question-analytics`);
      setAnalytics(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load analytics");
      setShowAnalytics(false);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const parseBulkQuestions = (text) => {
    // Accept CSV-ish lines: Question, Correct, Wrong1, Wrong2, Wrong3.
    // Also supports pipe `|` and tab as delimiters. Skips blank lines and a
    // header row starting with "question" (case-insensitive).
    const out = [];
    const errors = [];
    const rows = (text || "").split(/\r?\n/);
    rows.forEach((line, idx) => {
      const raw = line.trim();
      if (!raw) return;
      // Determine delimiter for this line — tab > pipe > comma
      let delim = ",";
      if (raw.includes("\t")) delim = "\t";
      else if (raw.includes("|")) delim = "|";
      const parts = raw.split(delim).map(s => s.trim());
      if (idx === 0 && parts[0].toLowerCase() === "question") return; // header
      if (parts.length < 5) {
        errors.push(`Line ${idx + 1}: need 5 columns (Question, Correct, Wrong1, Wrong2, Wrong3)`);
        return;
      }
      const [q, correct, w1, w2, w3] = parts;
      if (!q || !correct || !w1 || !w2 || !w3) {
        errors.push(`Line ${idx + 1}: empty field`);
        return;
      }
      const distinct = new Set([correct.toLowerCase(), w1.toLowerCase(), w2.toLowerCase(), w3.toLowerCase()]);
      if (distinct.size !== 4) {
        errors.push(`Line ${idx + 1}: answers must all be different`);
        return;
      }
      out.push({ question_text: q, correct_answer: correct, wrong_answers: [w1, w2, w3] });
    });
    return { out, errors };
  };

  const importBulkQuestions = () => {
    const { out, errors } = parseBulkQuestions(bulkText);
    if (errors.length) {
      toast.error(errors.slice(0, 3).join(" · ") + (errors.length > 3 ? ` … +${errors.length - 3} more` : ""));
      return;
    }
    if (!out.length) {
      toast.error("Nothing to import");
      return;
    }
    setEditForm(prev => ({ ...prev, questions: [...(prev.questions || []), ...out] }));
    setBulkText("");
    setShowBulk(false);
    toast.success(`Added ${out.length} question${out.length === 1 ? "" : "s"}`);
  };

  const handleRegenerateQuestions = async () => {
    if (!editForm?.id) return;
    setRegenerating(true);
    try {
      const res = await axios.post(`${API}/cocktails/${editForm.id}/regenerate-questions`);
      setEditForm(prev => ({ ...prev, questions: res.data.questions }));
      toast.success("Questions regenerated");
    } catch {
      toast.error("Failed to regenerate questions");
    } finally {
      setRegenerating(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editForm?.id) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/cocktails/${editForm.id}/upload-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setEditForm(prev => ({ ...prev, image_url: res.data.image_url }));
      toast.success("Image uploaded");
    } catch {
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!editForm.name) {
      toast.error("Name is required");
      return;
    }

    setSaving(true);
    try {
      const validIngredients = editForm.ingredients.filter(ing => ing.name || ing.measurement);
      const validBatchBreakdown = (editForm.batch_breakdown || []).filter(ing => ing.name || ing.measurement);
      const payload = {
        name: editForm.name,
        category: editForm.category || "Uncategorized",
        base_spirit: editForm.base_spirit || "Other",
        ingredients: validIngredients,
        garnish: editForm.garnish || "None",
        glassware: editForm.glassware || "",
        ice: editForm.ice || "",
        side_elements: editForm.side_elements || "",
        method: editForm.method || "",
        notes: editForm.notes || "",
        description: editForm.description || "",
        questions: editForm.questions || null,
        image_url: editForm.image_url || null,
        batch_ingredient_name: editForm.batch_ingredient_name || null,
        batch_breakdown: editForm.batch_ingredient_name ? validBatchBreakdown : [],
      };

      await axios.put(`${API}/cocktails/${editForm.id}`, payload);
      toast.success("Cocktail updated");
      closeEditMode();
      setSelectedCocktail(null);
      fetchCocktails();
    } catch (error) {
      toast.error("Failed to update cocktail");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cocktailId) => {
    if (!window.confirm("Are you sure you want to delete this cocktail?")) return;
    
    try {
      await axios.delete(`${API}/cocktails/${cocktailId}`);
      toast.success("Cocktail deleted");
      setSelectedCocktail(null);
      fetchCocktails();
    } catch (error) {
      toast.error("Failed to delete cocktail");
    }
  };

  return (
    <div data-testid="cocktails-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Cocktails</h1>
        <p className="text-[hsl(var(--muted-foreground))] mt-2">
          Browse and filter your cocktail library
        </p>
      </div>

      {/* Search & Filters */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" size={20} />
            <Input
              placeholder="Search cocktails..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-[hsl(var(--secondary))] border-[hsl(var(--border))] h-11"
              data-testid="cocktail-search-input"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2"
            data-testid="toggle-filters-btn"
          >
            <Filter size={18} />
            Filters
            {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </Button>
          {canEdit && (
            <Button
              variant={selectionMode ? "default" : "outline"}
              onClick={toggleSelectionMode}
              className={`flex items-center gap-2 ${selectionMode ? "bg-[hsl(var(--primary))] text-black" : ""}`}
              data-testid="toggle-selection-mode-btn"
            >
              {selectionMode ? <CheckSquare size={18} /> : <Square size={18} />}
              Select
            </Button>
          )}
        </div>

        {/* Selection toolbar */}
        {selectionMode && (
          <div className="flex items-center gap-2 flex-wrap p-3 rounded-lg bg-[hsl(var(--secondary))]/60 border border-[hsl(var(--border))]" data-testid="selection-toolbar">
            <span className="text-sm font-medium">{selectedIds.size} selected</span>
            <Button variant="outline" size="sm" onClick={selectAllVisible} data-testid="select-all-visible-btn">
              Select all {cocktails.length} visible
            </Button>
            <Button variant="outline" size="sm" onClick={clearSelection} data-testid="clear-selection-btn">
              Clear
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowBulkDeleteConfirm(true)}
              disabled={selectedIds.size === 0}
              className="ml-auto"
              data-testid="bulk-delete-btn"
            >
              <Trash2 size={14} className="mr-1" /> Delete Selected ({selectedIds.size})
            </Button>
          </div>
        )}

        {/* Filter Panel */}
        {showFilters && (
          <div className="p-4 rounded-lg bg-[hsl(var(--card))] border border-[hsl(var(--border))] space-y-4" data-testid="filter-panel">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="filter-category">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  {filters?.categories?.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={baseSpirit} onValueChange={setBaseSpirit}>
                <SelectTrigger data-testid="filter-base-spirit">
                  <SelectValue placeholder="Base Spirit" />
                </SelectTrigger>
                <SelectContent>
                  {filters?.base_spirits?.map((spirit) => (
                    <SelectItem key={spirit} value={spirit}>{spirit}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={ingredient} onValueChange={setIngredient}>
                <SelectTrigger data-testid="filter-ingredient">
                  <SelectValue placeholder="Ingredient / Brand" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__brands__" disabled className="font-semibold text-[hsl(var(--primary))]">
                    — Brands —
                  </SelectItem>
                  {filters?.ingredient_brands?.map((brand) => (
                    <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                  ))}
                  <SelectItem value="__ingredients__" disabled className="font-semibold text-[hsl(var(--primary))] mt-2">
                    — Ingredients —
                  </SelectItem>
                  {filters?.ingredient_names?.map((name) => (
                    <SelectItem key={name} value={name}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {canEdit && category && (
              <div className="flex items-center gap-2 flex-wrap pt-1 border-t border-[hsl(var(--border))] mt-2">
                <span className="text-sm text-[hsl(var(--muted-foreground))]">
                  Bulk questions for "{category}":
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportCategoryQuestionsCsv}
                  disabled={categoryExporting}
                  data-testid="category-export-questions-btn"
                >
                  <Download size={14} className="mr-1" /> {categoryExporting ? "Exporting..." : "Export CSV"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={triggerCategoryImport}
                  disabled={categoryImporting}
                  data-testid="category-import-questions-btn"
                >
                  <Upload size={14} className="mr-1" /> {categoryImporting ? "Importing..." : "Import CSV"}
                </Button>
                <Button
                  variant={selectionMode ? "default" : "outline"}
                  size="sm"
                  onClick={toggleSelectionMode}
                  className={selectionMode ? "bg-[hsl(var(--primary))] text-black" : ""}
                  data-testid="filter-panel-select-btn"
                >
                  {selectionMode ? <CheckSquare size={14} className="mr-1" /> : <Square size={14} className="mr-1" />} Select cocktails
                </Button>
                <input
                  ref={categoryFileInputRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleCategoryImportFile}
                  data-testid="category-import-questions-input"
                />
              </div>
            )}

            {hasActiveFilters && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-[hsl(var(--muted-foreground))]">Active:</span>
                {category && (
                  <span className="filter-badge">
                    {category}
                    <X size={14} className="cursor-pointer" onClick={() => setCategory("")} />
                  </span>
                )}
                {baseSpirit && (
                  <span className="filter-badge">
                    {baseSpirit}
                    <X size={14} className="cursor-pointer" onClick={() => setBaseSpirit("")} />
                  </span>
                )}
                {ingredient && (
                  <span className="filter-badge">
                    {ingredient}
                    <X size={14} className="cursor-pointer" onClick={() => setIngredient("")} />
                  </span>
                )}
                <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                  Clear All
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Results Count */}
      <p className="text-sm text-[hsl(var(--muted-foreground))]">
        Showing {cocktails.length} cocktail{cocktails.length !== 1 ? 's' : ''}
      </p>

      {/* Cocktail Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12" data-testid="cocktails-loading">
          <p className="text-[hsl(var(--muted-foreground))]">Loading cocktails...</p>
        </div>
      ) : cocktails.length === 0 ? (
        <div className="text-center py-12" data-testid="no-cocktails">
          <Wine size={48} className="mx-auto text-[hsl(var(--muted-foreground))] mb-4" />
          <p className="text-[hsl(var(--muted-foreground))]">No cocktails found</p>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            Try adjusting your filters or upload some specs
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cocktails-grid">
          {cocktails.map((cocktail) => (
            <Card
              key={cocktail.id}
              className={`cocktail-card bg-[hsl(var(--card))] border-[hsl(var(--border))] cursor-pointer overflow-hidden relative group ${selectionMode && selectedIds.has(cocktail.id) ? "ring-2 ring-[hsl(var(--primary))]" : ""}`}
              onClick={() => handleCardClick(cocktail)}
              onContextMenu={(e) => canEdit && handleCardContextMenu(e, cocktail)}
              onPointerDown={() => canEdit && startLongPress(cocktail)}
              onPointerUp={cancelLongPress}
              onPointerLeave={cancelLongPress}
              onPointerCancel={cancelLongPress}
              data-testid={`cocktail-card-${cocktail.id}`}
            >
              {canEdit && selectionMode && (
                <div className="absolute top-2 left-2 z-10 p-1 rounded bg-black/70" data-testid={`select-checkbox-${cocktail.id}`}>
                  <Checkbox
                    checked={selectedIds.has(cocktail.id)}
                    onCheckedChange={() => toggleSelectCocktail(cocktail.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="border-white"
                  />
                </div>
              )}
              {canEdit && !selectionMode && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(cocktail.id); }}
                  className="absolute top-2 right-2 p-2 rounded-full bg-black/70 text-white opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity hover:bg-red-500 active:bg-red-500 z-10"
                  aria-label="Delete cocktail"
                  data-testid={`delete-cocktail-${cocktail.id}`}
                >
                  <Trash2 size={14} />
                </button>
              )}
              {cocktail.image_url && (
                <div className="h-36 overflow-hidden bg-[hsl(var(--secondary))]">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${cocktail.image_url}`}
                    alt={cocktail.name}
                    className="w-full h-full object-cover"
                    data-testid={`cocktail-img-${cocktail.id}`}
                  />
                </div>
              )}
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <h3 className="text-lg font-semibold leading-tight">{cocktail.name}</h3>
                  <span className="text-xs px-2 py-1 rounded bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] whitespace-nowrap">
                    {cocktail.base_spirit}
                  </span>
                </div>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">{cocktail.category}</p>
                <div className="space-y-2 text-sm">
                  <p><span className="text-[hsl(var(--muted-foreground))]">Glass:</span> {cocktail.glassware}</p>
                  <p><span className="text-[hsl(var(--muted-foreground))]">Garnish:</span> {cocktail.garnish}</p>
                  <p className="text-[hsl(var(--muted-foreground))]">
                    {cocktail.ingredients?.length || 0} ingredients
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Cocktail Detail Modal */}
      <Dialog open={!!selectedCocktail && !editMode} onOpenChange={() => setSelectedCocktail(null)}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cocktail-detail-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-2xl">{selectedCocktail?.name}</DialogTitle>
              {canEdit && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEditMode(selectedCocktail)}
                    data-testid="edit-cocktail-btn"
                  >
                    <Edit size={16} className="mr-1" /> Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(selectedCocktail?.id)}
                    className="text-red-500 hover:text-red-400"
                    data-testid="delete-cocktail-btn"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              )}
            </div>
          </DialogHeader>
          {selectedCocktail && (
            <div className="space-y-4">
              {selectedCocktail.image_url && (
                <div className="rounded-lg overflow-hidden max-h-64 bg-[hsl(var(--secondary))]" data-testid="detail-cocktail-image">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${selectedCocktail.image_url}`}
                    alt={selectedCocktail.name}
                    className="w-full h-full object-cover max-h-64"
                  />
                </div>
              )}
              <div className="flex gap-2 flex-wrap items-center">
                <span className="text-xs px-2 py-1 rounded bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]">
                  {selectedCocktail.category}
                </span>
                <span className="text-xs px-2 py-1 rounded bg-[hsl(var(--secondary))] text-[hsl(var(--foreground))]">
                  {selectedCocktail.base_spirit}
                </span>
                {selectedCocktail.visibility && selectedCocktail.visibility !== "private" && (
                  <span
                    className={`text-xs px-2 py-1 rounded font-bold ${selectedCocktail.visibility === "system" ? "bg-yellow-500/15 text-yellow-300 border border-yellow-500/30" : "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"}`}
                    data-testid="visibility-badge"
                    title={selectedCocktail.visibility === "system" ? "Curated by FlashSpecs — visible to everyone" : "Public — visible to everyone"}
                  >
                    {selectedCocktail.visibility === "system" ? "★ System" : "Public"}
                  </span>
                )}
              </div>

              {canEdit && (
                <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/40 p-3" data-testid="visibility-controls">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-2">Visibility</p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant={selectedCocktail.visibility === "private" ? "default" : "outline"}
                      onClick={() => updateVisibility(selectedCocktail.id, "private")}
                      data-testid="vis-private"
                      className={selectedCocktail.visibility === "private" ? "bg-[hsl(var(--primary))] text-black" : ""}
                    >
                      Private
                    </Button>
                    <Button
                      size="sm"
                      variant={selectedCocktail.visibility === "public" ? "default" : "outline"}
                      onClick={() => updateVisibility(selectedCocktail.id, "public")}
                      data-testid="vis-public"
                      className={selectedCocktail.visibility === "public" ? "bg-emerald-500 text-black" : ""}
                    >
                      Public
                    </Button>
                    {isSystemAdmin && (
                      <Button
                        size="sm"
                        variant={selectedCocktail.visibility === "system" ? "default" : "outline"}
                        onClick={() => updateVisibility(selectedCocktail.id, "system")}
                        data-testid="vis-system"
                        className={selectedCocktail.visibility === "system" ? "bg-yellow-500 text-black" : ""}
                      >
                        ★ System
                      </Button>
                    )}
                  </div>
                  <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2 leading-snug">
                    {selectedCocktail.visibility === "private" && "Only your organisation can see this cocktail."}
                    {selectedCocktail.visibility === "public" && "Visible to everyone. Will appear on your org's public profile."}
                    {selectedCocktail.visibility === "system" && "Curated FlashSpecs content — visible to every user, everywhere."}
                  </p>
                </div>
              )}

              <div>
                <h4 className="font-semibold mb-2 text-[hsl(var(--primary))]">Ingredients</h4>
                <ul className="space-y-1">
                  {selectedCocktail.ingredients?.map((ing, idx) => (
                    <li key={idx} className="text-sm flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-[hsl(var(--primary))]" />
                      <span className="mono">{ing.measurement}</span>
                      <span>{ing.name}</span>
                      {ing.brand && <span className="text-[hsl(var(--muted-foreground))]">({ing.brand})</span>}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold mb-1 text-[hsl(var(--primary))]">Glassware</h4>
                  <p className="text-sm">{selectedCocktail.glassware || "—"}</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-1 text-[hsl(var(--primary))]">Garnish</h4>
                  <p className="text-sm">{selectedCocktail.garnish || "—"}</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-1 text-[hsl(var(--primary))]">Method</h4>
                  <p className="text-sm">{selectedCocktail.method || "—"}</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-1 text-[hsl(var(--primary))]">Ice</h4>
                  <p className="text-sm">{selectedCocktail.ice || "—"}</p>
                </div>
              </div>

              {selectedCocktail.side_elements && selectedCocktail.side_elements !== "N/A" && (
                <div>
                  <h4 className="font-semibold mb-1 text-[hsl(var(--primary))]">Side Elements</h4>
                  <p className="text-sm">{selectedCocktail.side_elements}</p>
                </div>
              )}

              {selectedCocktail.notes && (
                <div>
                  <h4 className="font-semibold mb-1 text-[hsl(var(--primary))]">Notes</h4>
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">{selectedCocktail.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Cocktail Modal */}
      <Dialog open={editMode} onOpenChange={closeEditMode}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-2xl max-h-[90vh] overflow-y-auto p-0" data-testid="edit-cocktail-dialog">
          <DialogHeader className="sticky top-0 z-10 bg-[hsl(var(--card))] border-b border-[hsl(var(--border))] px-6 py-4">
            <div className="flex items-center justify-between gap-3 pr-8">
              <DialogTitle>Edit Cocktail</DialogTitle>
              <div className="flex items-center gap-2 shrink-0">
                <Button variant="outline" size="sm" onClick={closeEditMode} data-testid="edit-cancel-btn-top">
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={saving}
                  className="btn-glow bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90"
                  data-testid="edit-save-btn-top"
                >
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </DialogHeader>
          {editForm && (
            <div className="space-y-4 px-6 pb-6">
              {/* Image Upload */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <ImageIcon size={16} className="text-[hsl(var(--primary))]" />
                  Cocktail Image
                </Label>
                <div className="flex items-center gap-4">
                  {editForm.image_url ? (
                    <div className="w-24 h-24 rounded-lg overflow-hidden bg-[hsl(var(--secondary))] shrink-0">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${editForm.image_url}`}
                        alt={editForm.name}
                        className="w-full h-full object-cover"
                        data-testid="edit-cocktail-image-preview"
                      />
                    </div>
                  ) : (
                    <div className="w-24 h-24 rounded-lg bg-[hsl(var(--secondary))] flex items-center justify-center shrink-0">
                      <ImageIcon size={24} className="text-[hsl(var(--muted-foreground))]" />
                    </div>
                  )}
                  <div>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        data-testid="edit-image-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={uploading}
                        asChild
                      >
                        <span>
                          <Upload size={14} className="mr-1" />
                          {uploading ? "Uploading..." : editForm.image_url ? "Change Image" : "Upload Image"}
                        </span>
                      </Button>
                    </label>
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">JPG, PNG, WebP. Max 10MB.</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                    className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]"
                    data-testid="edit-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Input
                    value={editForm.category}
                    onChange={(e) => setEditForm(prev => ({ ...prev, category: e.target.value }))}
                    className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]"
                    data-testid="edit-category"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Base Spirit</Label>
                  <Input
                    value={editForm.base_spirit}
                    onChange={(e) => setEditForm(prev => ({ ...prev, base_spirit: e.target.value }))}
                    className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]"
                    data-testid="edit-base-spirit"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Glassware</Label>
                  <LibraryAutocomplete
                    value={editForm.glassware}
                    onChange={(v) => setEditForm(prev => ({ ...prev, glassware: v }))}
                    items={getLibraryByType("glassware")}
                    placeholder="Search glassware..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Garnish</Label>
                  <LibraryAutocomplete
                    value={editForm.garnish}
                    onChange={(v) => setEditForm(prev => ({ ...prev, garnish: v }))}
                    items={getLibraryByType("garnish")}
                    placeholder="Search garnish..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Side Elements</Label>
                  <Input
                    value={editForm.side_elements}
                    onChange={(e) => setEditForm(prev => ({ ...prev, side_elements: e.target.value }))}
                    className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]"
                    data-testid="edit-side-elements"
                  />
                </div>
              </div>

              {/* Ingredients */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Ingredients</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addIngredient} data-testid="edit-add-ingredient">
                    <Plus size={16} className="mr-1" /> Add
                  </Button>
                </div>
                {editForm.ingredients.map((ing, idx) => {
                  const isBatch = editForm.batch_ingredient_name && ing.name && editForm.batch_ingredient_name === ing.name;
                  return (
                  <div key={idx} className="flex gap-2 items-start" data-testid={`edit-ingredient-${idx}`}>
                    <Input
                      placeholder="Measurement"
                      value={ing.measurement || ""}
                      onChange={(e) => updateIngredient(idx, "measurement", e.target.value)}
                      className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] w-28"
                    />
                    <div className="flex-1">
                      <LibraryAutocomplete
                        value={ing.name}
                        onChange={(v) => updateIngredient(idx, "name", v)}
                        items={getLibraryByType("ingredient")}
                        placeholder="Ingredient name"
                      />
                    </div>
                    <Input
                      placeholder="Brand (optional)"
                      value={ing.brand || ""}
                      onChange={(e) => updateIngredient(idx, "brand", e.target.value)}
                      className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] w-32"
                    />
                    <Button
                      type="button"
                      variant={isBatch ? "default" : "outline"}
                      size="sm"
                      disabled={!ing.name}
                      onClick={() => toggleBatchIngredient(ing)}
                      className={isBatch ? "bg-amber-600 hover:bg-amber-500 text-white whitespace-nowrap min-w-[110px] justify-center" : "whitespace-nowrap min-w-[110px] justify-center"}
                      title="Mark this line as the pre-mixed batch pour"
                      data-testid={`toggle-batch-ingredient-${idx}`}
                    >
                      {isBatch ? "Is Batch" : "Mark Batch"}
                    </Button>
                    {editForm.ingredients.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeIngredient(idx)}
                        className="text-red-500 hover:text-red-400"
                      >
                        <Trash2 size={18} />
                      </Button>
                    )}
                  </div>
                  );
                })}
              </div>

              {editForm.batch_ingredient_name && (
                <div className="space-y-3 p-3 rounded-lg border border-amber-600/40 bg-amber-950/10" data-testid="batch-breakdown-section">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-amber-500">Batch Breakdown — "{editForm.batch_ingredient_name}"</Label>
                      <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                        The original spirits that make up this batch, with their own measurements. Used only by the
                        "Full Recipe & Method" test — the recipe above still shows the single batch pour.
                      </p>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={addBatchBreakdownItem} data-testid="add-batch-breakdown-item">
                      <Plus size={16} className="mr-1" /> Add
                    </Button>
                  </div>
                  {(editForm.batch_breakdown || []).map((ing, idx) => (
                    <div key={idx} className="flex gap-2 items-start" data-testid={`batch-breakdown-ingredient-${idx}`}>
                      <Input
                        placeholder="Measurement"
                        value={ing.measurement || ""}
                        onChange={(e) => updateBatchBreakdownItem(idx, "measurement", e.target.value)}
                        className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] w-28"
                      />
                      <div className="flex-1">
                        <LibraryAutocomplete
                          value={ing.name}
                          onChange={(v) => updateBatchBreakdownItem(idx, "name", v)}
                          items={getLibraryByType("ingredient")}
                          placeholder="Ingredient name"
                        />
                      </div>
                      <Input
                        placeholder="Brand (optional)"
                        value={ing.brand || ""}
                        onChange={(e) => updateBatchBreakdownItem(idx, "brand", e.target.value)}
                        className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] w-32"
                      />
                      {editForm.batch_breakdown.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeBatchBreakdownItem(idx)}
                          className="text-red-500 hover:text-red-400"
                        >
                          <Trash2 size={18} />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-2">
                <Label>Method</Label>
                <LibraryAutocomplete
                  value={editForm.method}
                  onChange={(v) => setEditForm(prev => ({ ...prev, method: v }))}
                  items={getLibraryByType("method")}
                  placeholder="Search method..."
                />
              </div>

              <div className="space-y-2">
                <Label>Ice</Label>
                <LibraryAutocomplete
                  value={editForm.ice || ""}
                  onChange={(v) => setEditForm(prev => ({ ...prev, ice: v }))}
                  items={getLibraryByType("ice")}
                  placeholder="Search ice type..."
                />
              </div>

              <div className="space-y-2">
                <Label>Notes (Allergens)</Label>
                <Textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]"
                  data-testid="edit-notes"
                />
              </div>

              <div className="space-y-2">
                <Label>Guest Description</Label>
                <Textarea
                  value={editForm.description || ""}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="How staff should describe this cocktail to guests..."
                  className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]"
                  data-testid="edit-description"
                />
              </div>

              {/* Questions Editor */}
              <div className="space-y-3 pt-2 border-t border-[hsl(var(--border))]">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <Label className="text-base flex items-center gap-2">
                    <HelpCircle size={16} className="text-[hsl(var(--primary))]" />
                    Quiz Questions ({editForm.questions?.length || 0})
                  </Label>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addQuestion}
                      data-testid="add-question-row-btn"
                    >
                      <Plus size={14} className="mr-1" /> Add question
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowBulk(true)}
                      data-testid="bulk-import-btn"
                    >
                      <Upload size={14} className="mr-1" /> Bulk import
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={downloadQuestionsCsv}
                      data-testid="csv-download-btn"
                    >
                      <Download size={14} className="mr-1" /> CSV
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={openAnalytics}
                      data-testid="question-analytics-btn"
                    >
                      <BarChart2 size={14} className="mr-1" /> Analytics
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleRegenerateQuestions}
                      disabled={regenerating}
                      data-testid="regenerate-questions-btn"
                    >
                      <RefreshCw size={14} className={`mr-1 ${regenerating ? 'animate-spin' : ''}`} />
                      {regenerating ? "Regenerating..." : "Regenerate"}
                    </Button>
                  </div>
                </div>
                {editForm.questions?.length === 0 && (
                  <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-3">
                    No questions yet. Click Add question or Regenerate.
                  </p>
                )}
                {editForm.questions?.map((q, qIdx) => {
                  const totalQs = editForm.questions.length;
                  return (
                    <div key={qIdx} className="p-4 rounded-lg bg-[hsl(var(--secondary))] space-y-3" data-testid={`question-${qIdx}`}>
                      <div className="flex items-start gap-2">
                        <div className="flex flex-col gap-1 shrink-0">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => moveQuestion(qIdx, -1)}
                            disabled={qIdx === 0}
                            className="h-6 w-8 disabled:opacity-30"
                            aria-label="Move question up"
                            data-testid={`question-${qIdx}-up`}
                          >
                            <ChevronUp size={14} />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => moveQuestion(qIdx, 1)}
                            disabled={qIdx === totalQs - 1}
                            className="h-6 w-8 disabled:opacity-30"
                            aria-label="Move question down"
                            data-testid={`question-${qIdx}-down`}
                          >
                            <ChevronDown size={14} />
                          </Button>
                        </div>
                        <Input
                          value={q.question_text || ""}
                          onChange={(e) => updateQuestion(qIdx, "question_text", e.target.value)}
                          placeholder="Question"
                          className="bg-[hsl(var(--background))] border-[hsl(var(--border))] text-sm flex-1"
                          data-testid={`question-${qIdx}-text`}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeQuestion(qIdx)}
                          className="h-9 w-9 shrink-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          aria-label="Delete question"
                          data-testid={`question-${qIdx}-remove`}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-green-500 w-14 shrink-0 font-medium">Correct</span>
                          <Input
                            value={q.correct_answer || ""}
                            onChange={(e) => updateQuestion(qIdx, "correct_answer", e.target.value)}
                            className="bg-[hsl(var(--background))] border-green-500/30 h-8 text-sm"
                            data-testid={`question-${qIdx}-correct`}
                          />
                        </div>
                        {(q.wrong_answers || []).map((wa, waIdx) => (
                          <div key={waIdx} className="flex items-center gap-3">
                            <span className="text-xs text-red-400 w-14 shrink-0 font-medium">Wrong {waIdx + 1}</span>
                            <Input
                              value={wa}
                              onChange={(e) => updateWrongAnswer(qIdx, waIdx, e.target.value)}
                              className="bg-[hsl(var(--background))] border-red-500/20 h-8 text-sm"
                              data-testid={`question-${qIdx}-wrong-${waIdx}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Custom Questions (org-authored) — attached to this cocktail */}
              {editForm.id && (
                <div className="pt-4 border-t border-[hsl(var(--border))]">
                  <p className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <HelpCircle size={14} className="text-[hsl(var(--primary))]" />
                    Custom Questions for this cocktail
                  </p>
                  <QuestionsSection defaultCocktailId={editForm.id} embedded />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Import Questions dialog */}
      <Dialog open={showBulk} onOpenChange={setShowBulk}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-2xl" data-testid="bulk-import-dialog">
          <DialogHeader><DialogTitle>Bulk import questions</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              One question per line. Fields separated by comma, tab, or <code>|</code>. Header row optional.
            </p>
            <div className="rounded-md bg-[hsl(var(--secondary))]/60 p-3 text-xs font-mono leading-relaxed">
              Question, Correct, Wrong 1, Wrong 2, Wrong 3<br />
              What year was this invented?, 1874, 1901, 1932, 1885<br />
              Which country?, Peru, Chile, Argentina, Mexico
            </div>
            <Textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              placeholder="Paste your rows here..."
              className="bg-[hsl(var(--background))] min-h-[220px] font-mono text-sm"
              data-testid="bulk-import-textarea"
            />
            <div className="flex gap-2 pt-1">
              <Button onClick={importBulkQuestions} className="flex-1" data-testid="bulk-import-submit">
                <Upload size={14} className="mr-1" /> Import & add
              </Button>
              <Button variant="outline" onClick={() => setShowBulk(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Question Analytics dialog */}
      <Dialog open={showAnalytics} onOpenChange={setShowAnalytics}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="analytics-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart2 size={18} className="text-[hsl(var(--primary))]" />
              Question analytics — {editForm?.name || ""}
            </DialogTitle>
          </DialogHeader>
          {loadingAnalytics ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))] py-8 text-center">Loading…</p>
          ) : !analytics ? null : analytics.total_responses === 0 ? (
            <div className="py-8 text-center space-y-2">
              <p className="text-sm text-[hsl(var(--muted-foreground))]">No responses yet.</p>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Analytics populate as staff complete tests including this cocktail.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                Based on <span className="font-semibold text-[hsl(var(--foreground))]">{analytics.total_responses}</span> total responses across all tests including this cocktail. Sorted by lowest hit-rate so you can spot the ones tripping people up.
              </p>
              {[...analytics.questions]
                .sort((a, b) => {
                  if (a.responses === 0 && b.responses === 0) return 0;
                  if (a.responses === 0) return 1;
                  if (b.responses === 0) return -1;
                  return (a.hit_rate ?? 100) - (b.hit_rate ?? 100);
                })
                .map((q) => {
                  const pct = q.hit_rate;
                  const barColor = pct === null ? "bg-[hsl(var(--muted))]" : pct >= 80 ? "bg-green-500" : pct >= 60 ? "bg-[hsl(var(--primary))]" : "bg-red-500";
                  return (
                    <div key={q.index} className="rounded-md bg-[hsl(var(--secondary))]/60 p-3" data-testid={`analytics-row-${q.index}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <p className="text-sm font-medium flex-1">{q.question_text || <span className="italic text-[hsl(var(--muted-foreground))]">(no text)</span>}</p>
                        <span className={`text-sm font-bold shrink-0 ${pct === null ? "text-[hsl(var(--muted-foreground))]" : pct >= 80 ? "text-green-500" : pct >= 60 ? "text-[hsl(var(--primary))]" : "text-red-400"}`}>
                          {pct === null ? "—" : `${pct}%`}
                        </span>
                      </div>
                      <div className="relative h-2 rounded-full bg-[hsl(var(--background))] overflow-hidden">
                        <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct ?? 0}%` }} />
                      </div>
                      <div className="mt-1.5 flex items-center justify-between text-[10px] text-[hsl(var(--muted-foreground))]">
                        <span>{q.responses} response{q.responses === 1 ? "" : "s"}</span>
                        <span className="flex items-center gap-2">
                          <span className="text-green-500">{q.correct} correct</span>
                          <span>·</span>
                          <span className="text-red-400">{q.incorrect} wrong</span>
                        </span>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Category bulk-import results dialog */}
      <Dialog open={showImportSummary} onOpenChange={setShowImportSummary}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-lg max-h-[80vh] overflow-y-auto" data-testid="import-summary-dialog">
          <DialogHeader><DialogTitle>Import results — {category}</DialogTitle></DialogHeader>
          {importSummary && (
            <div className="space-y-4 text-sm">
              {importSummary.updated?.length > 0 && (
                <div>
                  <p className="font-medium text-green-500 mb-1">Updated ({importSummary.updated.length})</p>
                  <ul className="space-y-0.5 text-[hsl(var(--muted-foreground))]" data-testid="import-summary-updated">
                    {importSummary.updated.map((u, i) => (
                      <li key={i}>{u.name} — {u.count} question{u.count === 1 ? "" : "s"}</li>
                    ))}
                  </ul>
                </div>
              )}
              {importSummary.not_found?.length > 0 && (
                <div>
                  <p className="font-medium text-amber-500 mb-1">Not found in "{category}" ({importSummary.not_found.length})</p>
                  <ul className="space-y-0.5 text-[hsl(var(--muted-foreground))]" data-testid="import-summary-not-found">
                    {importSummary.not_found.map((n, i) => <li key={i}>{n}</li>)}
                  </ul>
                </div>
              )}
              {importSummary.errors?.length > 0 && (
                <div>
                  <p className="font-medium text-red-500 mb-1">Row errors ({importSummary.errors.length})</p>
                  <ul className="space-y-0.5 text-[hsl(var(--muted-foreground))]" data-testid="import-summary-errors">
                    {importSummary.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
              {!importSummary.updated?.length && !importSummary.not_found?.length && !importSummary.errors?.length && (
                <p className="text-[hsl(var(--muted-foreground))]">Nothing to import.</p>
              )}
              <Button variant="outline" onClick={() => setShowImportSummary(false)} data-testid="import-summary-close-btn">
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk delete confirmation dialog */}
      <Dialog open={showBulkDeleteConfirm} onOpenChange={setShowBulkDeleteConfirm}>
        <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md" data-testid="bulk-delete-confirm-dialog">
          <DialogHeader><DialogTitle>Delete {selectedIds.size} cocktail{selectedIds.size === 1 ? "" : "s"}?</DialogTitle></DialogHeader>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            This cannot be undone. All saved questions for these cocktails will also be removed.
          </p>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowBulkDeleteConfirm(false)} data-testid="bulk-delete-cancel-btn">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              data-testid="bulk-delete-confirm-btn"
            >
              {bulkDeleting ? "Deleting..." : `Delete ${selectedIds.size}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
